import { useEffect, useMemo, useState } from 'react'
import { Group, Stack } from '@mantine/core'
import KPI_Section from 'app/views/components/KPI/KPI-horizontal-section'
import { columnDefs } from './deals-table-columnDefs'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { DealParams } from 'app/models/deal-params'
import { dealScreenRowsAsParams } from 'app/models/deal-screen-row'
import { getKPIData } from './deals-kpi-data'
import MainTable from 'app/views/components/Table/MainTable'
import BulkActionsButton from 'app/views/components/buttons/bulk-actions-button'
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification'
import { approveBulkDeals, loadAllDeals, submitBulkDeals } from 'app/state/ducks/deals/thunks'
import { loadDealScreen } from 'app/state/ducks/deal-screen/thunks'
import { setDealScreenStatuses } from 'app/state/ducks/deal-screen/actions'
import {
  getDealScreenLoader,
  getDealScreenRows,
  isDealScreenStale,
} from 'app/state/ducks/deal-screen/selectors'
import { GridSortingStorage, GridFilterStorage, GridPaginationStorage, GridColumnVisibilityStorage, useGridSorting, useGridFilters, useGridPagination, useGridColumnVisibility } from 'app/utils/grid-sorting-storage'

/**
 * The Deals grid, from ONE request: GET /api/deal-management/screen returns
 * the rows the table renders — borrower and admin names and the Signing Portal
 * holds already resolved, an amendment in flight shown in place of the deal it
 * amends. The store follows the commands (the deals and deal-amendments
 * actions), so nothing is refetched after a write the rows can be patched for.
 *
 * The row menu's forms fetch the one deal they act on, by id, when they open.
 * Terminated deals are in the same read; the Terminated grid shows them.
 */
export default function DealManagmentPage() {
  const rows = useSelector(getDealScreenRows)
  const screenLoading = useSelector(getDealScreenLoader)
  const stale = useSelector(isDealScreenStale)
  const [bulkRunning, setBulkRunning] = useState(false)
  const globalLoading = screenLoading || bulkRunning
  // the Terminated grid has its own page; this one shows the live deals
  const deals = useMemo(
    () => dealScreenRowsAsParams(rows.filter(row => row.status !== 'Terminated')),
    [rows],
  )
  const [filteredDeals, setFilteredDeals] = useState<DealParams[]>(deals)
  const [selectedBulkDeals, setSelectedBulkDeals] = useState<DealParams[]>([])
  const [tableSelectedRows, setTableSelectedRows] = useState<Record<string, boolean>>({})

  // Grid sorting with localStorage persistence
  const { sorting, handleSortingChange } = useGridSorting(GridSortingStorage.deals)

  // Grid filters with localStorage persistence
  const { toMRTColumnFilters, handleMRTFiltersChange } = useGridFilters(GridFilterStorage.deals)

  // Grid pagination with localStorage persistence
  const { pagination, handlePaginationChange } = useGridPagination(GridPaginationStorage.deals)

  // Grid column visibility with localStorage persistence
  const { columnVisibility, handleColumnVisibilityChange } = useGridColumnVisibility(
    GridColumnVisibilityStorage.deals,
    ['systemId'] // Always hide systemId
  )

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const kpiData = getKPIData(deals, setFilteredDeals)

  useEffect(() => {
    dispatch(loadDealScreen())
  }, [])

  // A write the rows could not be patched for: reload once, while on screen.
  // Also runs when a load ends, for an invalidation that arrived during it.
  useEffect(() => {
    if (stale && !screenLoading) dispatch(loadDealScreen())
  }, [stale, screenLoading])

  useEffect(() => {
    setFilteredDeals(deals)
  }, [deals])

  /**
   * The bulk endpoints address a deal by its id and its key admin, both on the
   * row. They answer per deal; the rows the ledger accepted flip in place,
   * rather than being refetched while PQS may still be projecting them.
   */
  const runBulk = async (
    action: (deals: DealParams[]) => any,
    status: 'Submitted' | 'Approved',
    title: string,
  ) => {
    setBulkRunning(true)
    try {
      const result: any = await dispatch(action(selectedBulkDeals) as any)
      if (!result?.success) {
        ErrorNotification({ title: `${title} Failed`, message: result?.payload || `${title} failed` })
        return
      }
      const accepted = (Array.isArray(result.payload) ? result.payload : [])
        .filter((response: any) => response?.id && !response.ErrorMessage && !response.error)
        .map((response: any) => response.id as string)
      dispatch(setDealScreenStatuses({ ids: accepted, status }))
      SuccessNotification({
        title: `Successfully ${status} Bulk Deals`,
        message: 'This process does allow partial successes, double check for failures',
      })
      // The deal store the row menu's forms read follows, as it always did —
      // not awaited: the rows are already flipped, and the grid's store fills
      // the approved rows' system ids from it when it lands.
      dispatch(loadAllDeals())
    } finally {
      setBulkRunning(false)
    }
  }

  const bulkDealActions = {
    'Submit Deals': () => runBulk(submitBulkDeals, 'Submitted', 'Bulk Submit'),
    'Approve Deals': () => runBulk(approveBulkDeals, 'Approved', 'Bulk Approve'),
  }

  const handleRowSelection = (selectedRows: DealParams[]) => {
    setSelectedBulkDeals(selectedRows)
  }

  return (
    <Stack>
      <KPI_Section
        kpiData={kpiData}
        isLoading={globalLoading}
      />
      <Stack className='optionalTableHeader bulkActions'>
        <Group position="apart" w="100%">
          <BulkActionsButton selectedItems={selectedBulkDeals} clearSelectedItems={() => {
            setSelectedBulkDeals([]);
            setTableSelectedRows({})
          }} actions={bulkDealActions} />
        </Group>
      </Stack>
      {columnDefs.length != 0 ? (
        <MainTable
          tableName='Deals Table'
          isLoading={globalLoading}
          withPagination={true}
          csvExportEnabled={true}
          enableTopToolbar={false}
          headerBackgroundColor='#F0EEEE'
          columnDefs={columnDefs}
          enableRowSelection={true}
          enableMultiRowSelection={true}
          data={filteredDeals}
          tableSelectedRows={tableSelectedRows}
          setTableSelectedRows={setTableSelectedRows}
          hiddenColumns={['systemId']} //this is only used in export and not shown in the table
          initialSorting={[{ id: sorting.sortBy, desc: sorting.order === 'desc' }]}
          onSortingChange={handleSortingChange}
          initialColumnFilters={toMRTColumnFilters()}
          onColumnFiltersChange={handleMRTFiltersChange}
          initialPagination={{ pageSize: pagination.pageSize, pageIndex: pagination.pageIndex }}
          onPaginationChange={handlePaginationChange}
          initialColumnVisibility={columnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          setRow={(e: any, cellName: string) => {
            if (cellName != 'id') {
              navigate('/dealmanagement/deal/' + e.id + '/' + e.status)
            }
          }}
          selectedRows={handleRowSelection}
        />
      ) : null}
    </Stack>
  )
}